import { Box, Grid, IconButton, MenuItem, Typography } from "@mui/material";
import { Controller, FieldValues, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { format } from "date-fns";

import { ReactComponent as EditIcon } from "../../../../common/icons/editDark.svg";
import { ReactComponent as ConfirmIcon } from "../../../../common/icons/checkGreen.svg";
import { ReactComponent as CancelIcon } from "../../../../common/icons/crossRed.svg";
import { TextFieldLight } from "../../../../common/components/styled/styled";
import { leadingZerosDateFormat } from "../../../../common/helpers/formatDateTime";
import isEmpty from "../../../../common/helpers/isEmpty";

interface Props {
  name: string;
  label?: string;
  helperText?: string;
  rules: object;
  type?: "TextField" | "Select" | "Date";
  selectOptions?: { id: string; value: string; name: string }[];
  localization?: { [key: string]: string };
  onSave: (formValues: FieldValues) => void;
  children?: React.ReactNode;
  canEdit: boolean;
}

export const SingleEditFormRow = ({
  name,
  label,
  helperText,
  rules,
  type,
  selectOptions,
  localization,
  onSave,
  children,
  canEdit,
}: Props) => {
  const { t } = useTranslation("talentProfile");

  const [edit, setEdit] = useState<boolean>(false);

  const {
    control,
    formState: { errors, isDirty },
    reset,
  } = useFormContext();

  const isSaveDisabled = !isEmpty(errors) || !isDirty;

  const renderValue = (value: string | Date) => {
    if (value instanceof Date) {
      return format(value, leadingZerosDateFormat);
    }

    if (localization) {
      return localization[value];
    }

    return value || "-";
  };

  const save = (data: FieldValues) => {
    onSave(data);
    setEdit(false);
  };

  const cancel = () => {
    setEdit(false);
    reset();
  };

  const renderEditComponent = (
    value: any,
    onChange: (...event: any[]) => void,
  ) => (
    <Box display="flex" alignItems="center">
      {!children ? (
        type === "Date" ? (
          <DesktopDatePicker
            value={value}
            format={leadingZerosDateFormat}
            onChange={(newDate: Date | null) => onChange(newDate)}
            slotProps={{
              textField: {
                sx: { maxWidth: "135px" },
                size: "small",
                error: !!errors[name],
              },
            }}
          />
        ) : type === "TextField" ? (
          <TextFieldLight
            name={name}
            sx={{ maxWidth: "300px" }}
            fullWidth
            size="small"
            value={value}
            onChange={onChange}
            error={!!errors[name]}
          />
        ) : type === "Select" ? (
          <TextFieldLight
            select
            name={name}
            sx={{ maxWidth: "300px" }}
            fullWidth
            size="small"
            value={value}
            onChange={onChange}
            error={!!errors[name]}
          >
            {selectOptions?.map((option) => (
              <MenuItem key={option.id} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </TextFieldLight>
        ) : (
          <TextFieldLight
            name={name}
            sx={{ maxWidth: "300px" }}
            fullWidth
            size="small"
            value={value}
            onChange={onChange}
            error={!!errors[name]}
          />
        )
      ) : (
        <>{children}</>
      )}
      <IconButton onClick={cancel} sx={{ ml: "4px" }}>
        <CancelIcon />
      </IconButton>
      <IconButton
        onClick={save}
        disabled={isSaveDisabled}
        sx={{ ml: "4px", opacity: isSaveDisabled ? 0.5 : 1 }}
      >
        <ConfirmIcon />
      </IconButton>
    </Box>
  );

  return (
    <>
      <Grid item container xs={2} justifyContent="flex-end" alignItems="center">
        <Typography textAlign="end" variant="secondary">
          {label ? label : t(name)}
        </Typography>
      </Grid>
      <Grid item xs={10}>
        <Controller
          name={name}
          control={control}
          rules={rules}
          render={({ field: { value, onChange } }) =>
            edit ? (
              renderEditComponent(value, onChange)
            ) : (
              <Box display="flex" alignItems="center">
                <Typography>{renderValue(value)}</Typography>
                {canEdit && (
                  <IconButton
                    onClick={() => setEdit(true)}
                    sx={{ ml: "4px", height: "24px" }}
                  >
                    <EditIcon />
                  </IconButton>
                )}
              </Box>
            )
          }
        />
        {helperText && <Typography variant="body2">{helperText}</Typography>}
      </Grid>
    </>
  );
};
